import {
  Children,
  cloneElement,
  ComponentProps,
  isValidElement,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router";
import { NavLink as RouterLink } from "react-router-dom";
import clsx from "clsx";
import { Collapse } from "@material-ui/core";
import { ChevronUp, ChevronDown } from "lucide-react";

import { NavItemProps } from "./types";
import { NavItemFlyout } from "./NavItemFlyout";
import { NavNewBadge } from "./NavNewBadge";
import { useActiveDestinationCategory } from "./hooks/useActiveDestinationCategory";

const NavItem = ({
  title,
  href,
  exact = false,
  activeHrefPrefixes = [],
  collapsed = false,
  depth = 0,
  children,
  flag,
  newSince,
  ttlDays,
  featureKey,
  icon: Icon,
  className,
  tracking,
  open: openProp = false,
  label: Label,
  openNewTab = false,
  customActiveCheck,
  ...rest
}: PropsWithChildren<NavItemProps>) => {
  const location = useLocation();
  // In collapsed mode, don't auto-open based on route matching - only open on hover/focus
  const [open, setOpen] = useState(collapsed ? false : openProp);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const singleItemRef = useRef<HTMLLIElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleToggle = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  // Hover/focus handlers for collapsed flyout with delay to allow mouse travel
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear close timeout helper
  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  // Schedule close with delay
  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      setOpen(false);
    }, 150);
  }, [clearCloseTimeout]);

  const handleMouseEnter = useCallback(() => {
    if (collapsed && children) {
      clearCloseTimeout();
      closeTimeoutRef.current = setTimeout(() => {
        setOpen(true);
      }, 50);
    }
  }, [collapsed, children, clearCloseTimeout]);

  const handleMouseLeave = useCallback(() => {
    if (collapsed && children) {
      scheduleClose();
    }
  }, [collapsed, children, scheduleClose]);

  // Focus handlers for keyboard navigation in collapsed mode
  const handleButtonFocus = useCallback(() => {
    if (collapsed && children) {
      clearCloseTimeout();
      setOpen(true);
    }
  }, [collapsed, children, clearCloseTimeout]);

  const handleButtonBlur = useCallback(() => {
    if (collapsed && children) {
      // Delay to allow focus to move to flyout
      scheduleClose();
    }
  }, [collapsed, children, scheduleClose]);

  // Keyboard handler to move focus into flyout on Tab
  const handleButtonKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (collapsed && children && open && e.key === "Tab" && !e.shiftKey) {
        // Find the flyout in the DOM and focus its first focusable element
        const flyout = document.querySelector<HTMLElement>(
          "[data-navitem-flyout]",
        );
        if (flyout) {
          const focusable = flyout.querySelector<HTMLElement>(
            'a, button, [tabindex]:not([tabindex="-1"])',
          );
          if (focusable) {
            e.preventDefault();
            focusable.focus();
          }
        }
      }
    },
    [collapsed, children, open],
  );

  // Hover handlers for single items in collapsed mode (tooltip)
  const handleSingleItemMouseEnter = useCallback(() => {
    if (collapsed && !children && singleItemRef.current) {
      const rect = singleItemRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 16,
      });
      setShowTooltip(true);
    }
  }, [collapsed, children]);

  const handleSingleItemMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const isSubItem = depth > 0;
  const isComingSoon = flag === "coming soon";

  const activeDestinationCategory = useActiveDestinationCategory();

  const isActive: boolean = useMemo(() => {
    // Use custom active check if provided
    if (customActiveCheck) {
      return customActiveCheck();
    }

    const currentPath = window.location.pathname;

    // For destination navigation items, use smart category detection for /destinations/apps URLs
    if (
      activeHrefPrefixes.includes("/destinations/apps") &&
      currentPath.startsWith("/destinations/apps")
    ) {
      return href === activeDestinationCategory;
    }

    if (href) {
      if (exact) {
        return currentPath === href;
      }
      if (currentPath === href || currentPath.startsWith(href + "/")) {
        return true;
      }
    }

    if (activeHrefPrefixes.length > 0) {
      return activeHrefPrefixes.some((prefix) =>
        currentPath.startsWith(prefix),
      );
    }

    return false;
  }, [
    href,
    exact,
    activeHrefPrefixes,
    customActiveCheck,
    activeDestinationCategory,
  ]);

  const linkProps = useMemo(() => {
    if (isComingSoon) {
      return;
    }
    const props: ComponentProps<typeof RouterLink> = {
      to: href,
      exact,
      activeClassName: "bg-nav-background font-bold",
    };

    if (openNewTab) {
      props.target = "_blank";
      props.rel = "noopener noreferrer";
    }

    return props;
  }, [exact, href, isComingSoon, openNewTab]);

  // Base button classes
  const baseButtonClasses = clsx(
    `text-sm ${isSubItem ? "h-10" : "h-11"} px-3 flex items-center normal-case text-white w-full focus:outline focus:outline-nav-focus cursor-pointer transition-all duration-300 rounded-lg`,
    isSubItem
      ? "font-medium mb-1 pl-8 hover:bg-subnav-hover focus:bg-nav-background"
      : "font-semibold hover:bg-nav-hover focus:bg-nav-shell",
    isActive && "bg-nav-selected focus:bg-nav-selected",
    isComingSoon && "opacity-50 cursor-not-allowed hover:bg-transparent",
    collapsed ? "justify-center" : "justify-start",
  );

  const flagContent = useMemo(() => {
    if (newSince) {
      return (
        <NavNewBadge
          featureKey={featureKey}
          newSince={newSince}
          ttlDays={ttlDays}
        />
      );
    }

    return (
      isComingSoon && (
        <span className="rounded-sm bg-white p-1 text-[10px] font-medium text-nowrap text-black uppercase">
          {flag}
        </span>
      )
    );
  }, [flag, isComingSoon, newSince, featureKey, ttlDays]);

  const content = useMemo(() => {
    return (
      <>
        {Icon && (
          <Icon
            size={20}
            className={clsx(
              "flex-shrink-0 items-center text-gray-200 transition-[margin] duration-300",
              !collapsed && "mr-2",
            )}
            fontSize="inherit"
            aria-hidden="true"
          />
        )}
        <span
          className={clsx(
            "flex flex-1 items-center overflow-hidden text-left whitespace-nowrap transition-[opacity,max-width] duration-300",
            collapsed ? "max-w-0 opacity-0" : "max-w-full opacity-100",
          )}
        >
          <span className="flex w-full items-center justify-between gap-2">
            <span>{title}</span>
            {flagContent}
          </span>
          {Label && (
            <span className="ml-auto flex items-center">
              <Label />
            </span>
          )}
        </span>
      </>
    );
  }, [collapsed, flagContent, Icon, Label, title]);

  // Track previous collapsed state
  const prevCollapsedRef = useRef(collapsed);

  // Close dropdown only when transitioning from expanded to collapsed
  useEffect(() => {
    if (collapsed && !prevCollapsedRef.current) {
      setOpen(false);
    }
    prevCollapsedRef.current = collapsed;
  }, [collapsed]);

  // Sync open state when openProp changes (e.g., route navigation activates/deactivates a child)
  useEffect(() => {
    if (!collapsed) {
      setOpen(openProp);
    }
  }, [collapsed, openProp, location.pathname]);

  // Clone children with collapsed=false for flyout display
  const flyoutChildren = useMemo(() => {
    const cloneWithCollapsedFalse = (node: ReactNode): ReactNode => {
      return Children.map(node, (child) => {
        if (!isValidElement(child)) return child;

        const newProps: Record<string, unknown> = {};

        // Only add collapsed prop to elements that already have it (NavItem components)
        if ("collapsed" in child.props) {
          newProps.collapsed = false;
        }

        // Recursively handle children
        if (child.props.children) {
          newProps.children = cloneWithCollapsedFalse(child.props.children);
        }

        return cloneElement(child, newProps);
      });
    };

    return cloneWithCollapsedFalse(children);
  }, [children]);

  if (children) {
    return (
      <li
        {...rest}
        className={clsx("relative block py-0", className)}
        key={title}
        role="none"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => {
          if (collapsed && open) {
            clearCloseTimeout();
            setOpen(false);
          }
        }}
      >
        <button
          ref={buttonRef}
          className={clsx(
            baseButtonClasses,
            open &&
              !collapsed &&
              "bg-nav-background rounded-b-none focus:rounded-lg",
            open && collapsed && "bg-nav-background",
            // Show selected state when collapsed and initially had a matching child (openProp)
            collapsed && !open && openProp && "bg-nav-selected",
          )}
          onClick={isComingSoon || collapsed ? undefined : handleToggle}
          onFocus={handleButtonFocus}
          onBlur={handleButtonBlur}
          onKeyDown={handleButtonKeyDown}
          disabled={isComingSoon}
          role="menuitem"
          aria-haspopup="true"
          aria-expanded={open}
          data-track={tracking}
        >
          {Icon && (
            <Icon
              size={20}
              className={clsx(
                "flex-shrink-0 items-center text-gray-200 transition-[margin] duration-300",
                !collapsed && "mr-2",
              )}
              aria-hidden="true"
            />
          )}
          <span
            className={clsx(
              "flex w-full flex-1 items-center overflow-hidden text-left whitespace-nowrap transition-[opacity,max-width] duration-300",
              collapsed ? "max-w-0 opacity-0" : "max-w-full opacity-100",
            )}
          >
            <span className="flex w-full items-center justify-between gap-2">
              <span>{title}</span>
              {flagContent}
            </span>
            {!isComingSoon && (
              <span className="ml-auto flex-shrink-0">
                {open ? (
                  <ChevronUp size={16} className="text-gray-200" />
                ) : (
                  <ChevronDown size={16} className="text-gray-200" />
                )}
              </span>
            )}
          </span>
        </button>
        {/* Expanded state: inline collapse */}
        {!isComingSoon && !collapsed && (
          <Collapse in={open}>
            <div className="bg-nav-background rounded-b-lg px-2">
              {children}
            </div>
          </Collapse>
        )}
        {/* Collapsed state: flyout menu */}
        {!isComingSoon && collapsed && open && (
          <NavItemFlyout
            title={title}
            flagContent={flagContent}
            buttonRef={buttonRef}
            onClose={() => setOpen(false)}
            scheduleClose={scheduleClose}
            clearCloseTimeout={clearCloseTimeout}
          >
            {flyoutChildren}
          </NavItemFlyout>
        )}
      </li>
    );
  }

  // In rare cases, a nav item might link to an external HTTPS URL. Using the
  // react-router-dom <Link /> component for external links doesn't work, so
  // we fall back to a standard <a> tag for these links.
  if (href && href.startsWith("https")) {
    return (
      <li
        {...rest}
        ref={singleItemRef}
        className={clsx("flex py-0", className)}
        key={title}
        role="none"
        onMouseEnter={handleSingleItemMouseEnter}
        onMouseLeave={handleSingleItemMouseLeave}
      >
        <a
          className={clsx(baseButtonClasses, `depth-${depth}`)}
          role="menuitem"
          aria-disabled={isComingSoon}
          data-track={tracking}
          href={href}
          target={openNewTab ? "_blank" : undefined}
          rel={openNewTab ? "noopener noreferrer" : undefined}
        >
          {content}
        </a>
        {collapsed &&
          showTooltip &&
          createPortal(
            <div
              className="bg-nav-shell fixed z-[9999] rounded-md px-3 py-2 whitespace-nowrap text-white shadow-lg"
              style={{
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                transform: "translateY(-50%)",
              }}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span>{title}</span>
                {flagContent}
              </span>
            </div>,
            document.body,
          )}
      </li>
    );
  }

  // Single item
  return (
    <li
      {...rest}
      ref={singleItemRef}
      className={clsx("flex py-0", className)}
      key={title}
      role="none"
      onMouseEnter={handleSingleItemMouseEnter}
      onMouseLeave={handleSingleItemMouseLeave}
    >
      {linkProps ? (
        <RouterLink
          className={clsx(baseButtonClasses, `depth-${depth}`)}
          role="menuitem"
          aria-disabled={isComingSoon}
          data-track={tracking}
          {...linkProps}
        >
          {content}
        </RouterLink>
      ) : (
        <button
          className={clsx(baseButtonClasses, `depth-${depth}`)}
          role="menuitem"
          disabled={isComingSoon}
          data-track={tracking}
        >
          {content}
        </button>
      )}
      {collapsed &&
        showTooltip &&
        createPortal(
          <div
            className="bg-nav-shell fixed z-[9999] rounded-md px-3 py-2 whitespace-nowrap text-white shadow-lg"
            style={{
              top: tooltipPosition.top,
              left: tooltipPosition.left,
              transform: "translateY(-50%)",
            }}
          >
            {title}
            {flagContent}
          </div>,
          document.body,
        )}
    </li>
  );
};

export default NavItem;
